/**
 * Utility functions for formatting data in the application
 */

/*
 * Salary lives in two columns with two different units, so it gets two
 * formatters — never one shared "format a salary" helper.
 *
 *   jobs.salaryMin / salaryMax            whole LPA   -> formatLpa
 *   candidates.currentSalary / expectedSalary  annual rupees -> formatAnnualCtc
 *
 * They were previously formatted by the same formatCurrency, which reads its
 * argument as rupees. A job stored as 2 LPA therefore rendered "₹2", and
 * formatSalary appended " PA" to produce "₹2 - ₹5 PA" on the job detail page.
 * Keeping the units in separate functions is what stops one column being
 * rendered in the other one's unit.
 */

/** Job pay range, stored in whole lakhs per annum. */
export const formatLpa = (min, max) => {
    const lo = min === null || min === undefined || min === '' ? null : Number(min);
    const hi = max === null || max === undefined || max === '' ? null : Number(max);
    const has = (v) => v !== null && Number.isFinite(v) && v > 0;

    if (!has(lo) && !has(hi)) return 'Not disclosed';
    if (has(lo) && !has(hi)) return `₹${lo}+ LPA`;
    if (!has(lo) && has(hi)) return `Up to ₹${hi} LPA`;
    if (lo === hi) return `₹${lo} LPA`;
    return `₹${lo} – ₹${hi} LPA`;
};

/*
 * Candidate CTC, stored as rupees per year.
 *
 * "p.a." is always printed. Six different screens used to show this amount
 * with no period at all, so "₹50,000" could be read as a monthly salary — and
 * for some rows that is genuinely what the candidate meant when they typed it.
 * Stating the period is what makes a wrong row visibly wrong instead of
 * plausibly right.
 */
export const formatAnnualCtc = (value) => {
    if (value === null || value === undefined || value === '') return '';
    const n = Number(value);
    if (!Number.isFinite(n) || n <= 0) return '';
    return `₹${n.toLocaleString('en-IN')} p.a.`;
};

/**
 * Format salary range for display
 * @param {Object} salary - Salary object with min and max values
 * @returns {String} Formatted salary string
 * @deprecated Reads its values as rupees while jobs store LPA. Use formatLpa
 *   for a job pay range or formatAnnualCtc for a candidate CTC.
 */
export const formatSalary = (salary) => {
    if (!salary) return 'Not disclosed';
    
    if (salary.min === 0 && salary.max === 0) {
      return 'Not disclosed';
    }
    
    // If only one value is provided
    if (salary.min === salary.max) {
      return formatCurrency(salary.min) + ' PA';
    }
    
    // If min is 0, show "Up to X"
    if (salary.min === 0) {
      return `Up to ${formatCurrency(salary.max)} PA`;
    }
    
    // If max is 0, show "X+"
    if (salary.max === 0) {
      return `${formatCurrency(salary.min)}+ PA`;
    }
    
    // Regular salary range
    return `${formatCurrency(salary.min)} - ${formatCurrency(salary.max)} PA`;
  };
  
  /**
   * Format currency values (in lakhs or crores for INR)
   * @param {Number} amount - Amount to format
   * @returns {String} Formatted currency string
   */
  export const formatCurrency = (amount) => {
    // Format in lakhs and crores (Indian numbering system)
    if (amount >= (10000000)) {
      return `₹${(amount / 10000000).toFixed(2)} Cr`;
    } else if (amount >= 100000) {
      return `₹${(amount / 100000).toFixed(2)} LPA`;
    } else {
      return `₹${amount.toLocaleString('en-IN')}`;
    }
  };
  
  /**
   * Format date to display in readable format
   * @param {String} dateString - Date string to format
   * @returns {String} Formatted date string
   */
  export const formatDate = (dateString) => {
    if (!dateString) return '';
    
    const date = new Date(dateString);
    
    // Return formatted date: "Apr 15, 2025"
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };
  
  /**
   * Calculate days since job was posted
   * @param {String} postedDate - Date when job was posted
   * @returns {Number} Number of days since posting
   */
  export const calculatePostedDays = (postedDate) => {
    if (!postedDate) return 0;
    
    const posted = new Date(postedDate);
    const today = new Date();
    
    // Calculate difference in milliseconds
    const diffTime = Math.abs(today - posted);
    
    // Convert to days
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    
    return diffDays;
  };
  
  /**
   * Format experience range for display
   * @param {Object} experience - Experience object with min and max values
   * @returns {String} Formatted experience string
   */
  export const formatExperience = (experience) => {
    if (!experience) return 'Not specified';
    
    // If only one value is provided or they're the same
    if (experience.min === experience.max) {
      return `${experience.min} ${experience.min === 1 ? 'Year' : 'Years'}`;
    }
    
    // If min is 0, show "Up to X years"
    if (experience.min === 0) {
      return `Up to ${experience.max} ${experience.max === 1 ? 'Year' : 'Years'}`;
    }
    
    // Regular experience range
    return `${experience.min} - ${experience.max} Years`;
  };
  
  /**
   * Format file size for display
   * @param {Number} bytes - File size in bytes
   * @returns {String} Formatted file size
   */
  export const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };
  
  /**
   * Truncate text to a specified length
   * @param {String} text - Text to truncate
   * @param {Number} maxLength - Maximum length before truncation
   * @returns {String} Truncated text with ellipsis if needed
   */
  export const truncateText = (text, maxLength = 100) => {
    if (!text) return '';
    if (text.length <= maxLength) return text;
    
    return text.substring(0, maxLength) + '...';
  };
  
  /**
   * Format number with commas
   * @param {Number} number - Number to format
   * @returns {String} Formatted number with commas
   */
  export const formatNumber = (number) => {
    return Number(number).toLocaleString('en-IN');
  };
  
  /**
   * Format application status for display with color coding
   * @param {String} status - Application status
   * @returns {Object} Formatted status with text and color
   */
  export const formatApplicationStatus = (status) => {
    if (!status) return { text: 'Unknown', color: 'gray' };
    
    const statusMap = {
      'applied': { text: 'Applied', color: 'blue' },
      'screening': { text: 'Screening', color: 'purple' },
      'interview': { text: 'Interview', color: 'orange' },
      'selected': { text: 'Selected', color: 'green' },
      'rejected': { text: 'Rejected', color: 'red' },
      'on-hold': { text: 'On Hold', color: 'yellow' }
    };
    
    return statusMap[status.toLowerCase()] || { text: status, color: 'gray' };
  };
  
  /**
   * Format phone number with country code
   * @param {String} phone - Phone number
   * @param {String} countryCode - Country code (default: +91 for India)
   * @returns {String} Formatted phone number
   */
  export const formatPhoneNumber = (phone, countryCode = '+91') => {
    if (!phone) return '';
    
    // Remove all non-digit characters
    const cleaned = ('' + phone).replace(/\D/g, '');
    
    // Check if country code is already included
    if (cleaned.length === 10) {
      return `${countryCode} ${cleaned.substring(0, 5)}-${cleaned.substring(5)}`;
    }
    
    return phone;
  };
  
  export default {
    formatLpa,
    formatAnnualCtc,
    formatSalary,
    formatCurrency,
    formatDate,
    calculatePostedDays,
    formatExperience,
    formatFileSize,
    truncateText,
    formatNumber,
    formatApplicationStatus,
    formatPhoneNumber
  };

/*
 * jobs.jobType is stored as the ENUM value — 'full-time', 'part-time' — and was
 * being rendered raw in seven places, so the job detail page showed
 * "Employment Type: full-time" to every visitor.
 *
 * ONE map, because there were already three: a JOB_TYPE_LABELS in pages/Jobs.jsx,
 * a titleCaseJobType() in recruiter/ManageJobs.jsx, and a jobTypeLabels in the
 * API's job.controller.js. Three copies of a five-entry map is how "Full Time"
 * on one screen becomes "Full-Time" on the next.
 *
 * Why a MAP and not a title-caser: 'full-time' title-cased is "Full-Time", with
 * the hyphen still in it. The hyphen is a storage detail, not something a
 * candidate should read.
 *
 * Unknown values pass through tidied rather than blank. jobType is an ENUM so a
 * surprise should be impossible, but older rows were written before the ENUM
 * was enforced and a blank cell reads as missing data rather than odd data.
 */
const JOB_TYPE_LABELS = {
    'full-time': 'Full Time',
    'part-time': 'Part Time',
    'contract': 'Contract',
    'internship': 'Internship',
    'remote': 'Remote',
};

export const formatJobType = (value) => {
    const raw = String(value ?? '').trim();
    if (raw === '') return '';
    const known = JOB_TYPE_LABELS[raw.toLowerCase()];
    if (known) return known;
    // Unknown: split on hyphens/underscores and capitalise each word, so
    // 'freelance_contract' reads as "Freelance Contract".
    return raw
        .split(/[-_\s]+/)
        .filter(Boolean)
        .map(w => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())
        .join(' ');
};
